import React, { useEffect, useState, useMemo } from "react";
import {
    Box, CssBaseline, Breadcrumbs, Link, Typography, Stack, Button,
    Paper, TextField, InputAdornment, IconButton,
    TableContainer, Table, TableHead, TableRow, TableCell,
    TableBody, TableSortLabel, CircularProgress, Tooltip,
    Chip, Dialog, DialogTitle, DialogContent, DialogActions, Snackbar, Alert,
    Avatar
} from "@mui/material";
import {
    Search, Add, DeleteOutline, WarningAmber,
    FilterList,
    Pageview
} from '@mui/icons-material';
import { useNavigate } from "react-router-dom";
import Header from "../../component/header/Header";

const colors = {
    pageBg: '#F8F9FA',
    sidebarBg: '#F1F2F6',
    filterBg: "#FFF9E6",
    accent: '#FFC107',
    accentDark: '#FFA000',
    tableHeader: '#FDF7E2',
    textMain: '#5D4037'
};

const StaffListScreen = () => {
    const navigation = useNavigate();

    // --- State ---
    const [staffList, setStaffList] = useState([]);
    const [loading, setLoading] = useState(false);
    const [loadingDelete, setLoadingDelete] = useState(false);

    // Universal Search & Sorting
    const [filterSearch, setFilterSearch] = useState("");
    const [order, setOrder] = useState('asc');
    const [orderBy, setOrderBy] = useState('fullname');

    // UI State
    const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
    const [tempDeleteData, setTempDeleteData] = useState(null);
    const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

    useEffect(() => {
        getStaffList();
        localStorage.setItem("current_tab", "Staff");
    }, []);

    const getStaffList = async () => {
        setLoading(true);
        try {
            const response = await fetch(`https://www.geniusmileniaeducation.link/db-api/staff`);
            const resJson = await response.json();
            if (resJson.status === 1) {
                setStaffList(resJson.data);
            } else {
                setSnackbar({ open: true, message: resJson.message, severity: "error" });
            }
        } catch (error) {
            setSnackbar({ open: true, message: "Connection Error", severity: "error" });
        } finally {
            setLoading(false);
        }
    };

    const deleteStaff = async (staffId) => {
        setLoadingDelete(true);
        try {
            const response = await fetch(`https://www.geniusmileniaeducation.link/db-api/staff/${staffId}`, {
                method: 'DELETE'
            });
            const resJson = await response.json();
            if (resJson.status === 1) {
                setSnackbar({ open: true, message: "Staff deleted successfully", severity: "success" });
                setOpenDeleteDialog(false);
                getStaffList();
            } else {
                alert(resJson.message);
            }
        } catch (error) {
            setSnackbar({ open: true, message: "Delete failed", severity: "error" });
        } finally {
            setLoadingDelete(false);
        }
    };

    // --- Table Logic ---
    const handleSortRequest = (property) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    // Universal Search Logic: Checks fullname, mobileNo, and role
    const filteredAndSortedData = useMemo(() => {
        return staffList
            .filter((item) => {
                const searchStr = filterSearch.toLowerCase();
                return (
                    item.fullname?.toLowerCase().includes(searchStr) ||
                    item.mobileNo?.toLowerCase().includes(searchStr) ||
                    item.role?.toLowerCase().includes(searchStr)
                );
            })
            .sort((a, b) => {
                const aValue = a[orderBy] || "";
                const bValue = b[orderBy] || "";
                return order === 'asc' ? (aValue < bValue ? -1 : 1) : (bValue < aValue ? -1 : 1);
            });
    }, [staffList, filterSearch, order, orderBy]);

    const columns = [
        { id: 'fullname', name: 'Staff Name' },
        { id: 'mobileNo', name: 'Mobile Number' },
        { id: 'role', name: 'Role' },
        { id: 'status', name: 'Status' }
    ];

    const getStatusChip = (status) => {
        const isActive = status === "A";
        return (
            <Chip
                label={isActive ? "Active" : "Inactive"}
                color={isActive ? "success" : "default"}
                size="small"
                sx={{ fontWeight: 'bold' }}
            />
        );
    };

    return (
        <Box sx={{ display: 'flex', bgcolor: colors.sidebarBg, minHeight: '100vh' }}>
            <CssBaseline />
            <Box component="main" sx={{ flexGrow: 1, p: 3, bgcolor: "white", m: 2, borderRadius: 5 }}>

                <Breadcrumbs sx={{ mb: 2 }}>
                    <Link underline="hover" color="inherit" onClick={() => navigation("/Portal")} sx={{ cursor: 'pointer' }}>Portal</Link>
                    <Typography color="text.primary">Staff</Typography>
                </Breadcrumbs>

                <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Header title="Staff Directory" />
                    <Button
                        variant="contained"
                        startIcon={<Add />}
                        onClick={() => navigation("/Portal/Staff/Register")}
                        sx={{ bgcolor: colors.accent, color: colors.textMain, borderRadius: 2, '&:hover': { bgcolor: colors.accentDark } }}
                    >
                        Add Staff
                    </Button>
                </Stack>

                <Paper elevation={0} sx={{ p: 2, mb: 3, bgcolor: colors.filterBg, borderRadius: 3, display: 'flex', gap: 2, alignItems: 'center' }}>
                    <Stack direction="row" spacing={2} alignItems="center" sx={{ width: '100%' }}>
                        <FilterList sx={{ color: colors.textMain }} />
                        <TextField
                            fullWidth
                            size="small"
                            placeholder="Search by name, mobile, or role..."
                            value={filterSearch}
                            onChange={(e) => setFilterSearch(e.target.value)}
                            sx={{ bgcolor: 'white', borderRadius: 2, maxWidth: 400 }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Search fontSize="small" />
                                    </InputAdornment>
                                )
                            }}
                        />
                    </Stack>
                </Paper>

                <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #F0E6CC', borderRadius: 4, maxHeight: '65vh' }}>
                    <Table stickyHeader>
                        <TableHead>
                            <TableRow>
                                {columns.map((column) => (
                                    <TableCell
                                        key={column.id}
                                        sx={{ bgcolor: colors.tableHeader, fontWeight: 'bold' }}
                                    >
                                        <TableSortLabel
                                            active={orderBy === column.id}
                                            direction={orderBy === column.id ? order : 'asc'}
                                            onClick={() => handleSortRequest(column.id)}
                                        >
                                            {column.name}
                                        </TableSortLabel>
                                    </TableCell>
                                ))}
                                <TableCell align="center" sx={{ bgcolor: colors.tableHeader, fontWeight: 'bold' }}>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading ? (
                                <TableRow><TableCell colSpan={5} align="center" sx={{ py: 3 }}><CircularProgress size={30} /></TableCell></TableRow>
                            ) : filteredAndSortedData.length === 0 ? (
                                <TableRow><TableCell colSpan={5} align="center" sx={{ py: 3 }}>No staff records found.</TableCell></TableRow>
                            ) : filteredAndSortedData.map((row) => (
                                <TableRow key={row.staffId} hover>
                                    <TableCell>
                                        <Stack direction="row" spacing={2} alignItems="center">
                                            <Avatar sx={{ bgcolor: colors.accent, color: 'black', fontSize: 14 }}>
                                                {row.fullname?.split(" ")[0]?.charAt(0)}{row.fullname?.split(" ")[1]?.charAt(0)}
                                            </Avatar>
                                            <Typography variant="subtitle2" fontWeight="bold">{row.fullname}</Typography>
                                        </Stack>
                                    </TableCell>
                                    <TableCell>{row.mobileNo}</TableCell>
                                    <TableCell>
                                        <Chip label={row.role} size="small" variant="outlined" />
                                    </TableCell>
                                    <TableCell>{getStatusChip(row.status)}</TableCell>
                                    <TableCell align="center">
                                        <Stack direction="row" spacing={1} justifyContent="center">
                                            <Tooltip title="View Detail">
                                                <IconButton size="small" onClick={() => navigation(`/Portal/Staff/EditStaff/${row.staffId}`)}>
                                                    <Pageview fontSize="small" color="action" />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Delete">
                                                <IconButton
                                                    size="small"
                                                    color="error"
                                                    onClick={() => { setTempDeleteData(row); setOpenDeleteDialog(true); }}
                                                >
                                                    <DeleteOutline fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        </Stack>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Box>

            {/* --- DELETE DIALOG --- */}
            <Dialog open={openDeleteDialog} onClose={() => setOpenDeleteDialog(false)} PaperProps={{ sx: { borderRadius: 4 } }}>
                <Box sx={{ p: 3, textAlign: 'center' }}>
                    <WarningAmber sx={{ fontSize: 60, color: 'error.main', mb: 2 }} />
                    <DialogTitle sx={{ fontWeight: 'bold', p: 0 }}>Confirm Delete</DialogTitle>
                    <DialogContent sx={{ mt: 2 }}>
                        <Typography>Are you sure you want to delete <b>{tempDeleteData?.fullname}</b>?</Typography>
                        <Typography variant="caption" color="text.secondary">This action cannot be undone.</Typography>
                    </DialogContent>
                    <DialogActions sx={{ justifyContent: 'center', gap: 2, mt: 2 }}>
                        <Button onClick={() => setOpenDeleteDialog(false)} color="inherit" variant="outlined" sx={{ borderRadius: 2 }}>Cancel</Button>
                        <Button
                            variant="contained"
                            color="error"
                            onClick={() => deleteStaff(tempDeleteData.staffId)}
                            disabled={loadingDelete}
                            sx={{ borderRadius: 2, fontWeight: 'bold' }}
                        >
                            {loadingDelete ? <CircularProgress size={20} /> : "Delete Staff"}
                        </Button>
                    </DialogActions>
                </Box>
            </Dialog>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert severity={snackbar.severity} variant="filled" sx={{ borderRadius: 2 }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default StaffListScreen;